// Página de categoria de colunas (`/[language]/colunas/[id]/[slug]`): quando
// responder 404, quando consolidar a URL com 301 e o que ler da resposta do
// `v1/colunas/getColumns`.
//
// Lista vazia não decide 404: o deslocamento da listagem no desktop devolve
// lista vazia para categoria com até 4 artigos. Quem decide é o campo
// `categoria` da API, com o `total` sem deslocamento.

// `articletag` que a busca usa para "todas as colunas"
const ID_TODAS_AS_COLUNAS = 9999;

export interface CategoriaColuna {
  id: number;
  name: string;
  slug: string;
  slugs: { "pt-br": string; en: string };
  total: number;
  excluida: boolean;
}

export interface RespostaColunas {
  posts: any[];
  categoria: CategoriaColuna | null;
  // A resposta traz o campo `categoria`. Sem ele (API anterior ao BE-1), a
  // página não decide 404 nem 301 e fica como antes.
  temMeta: boolean;
}

export type DecisaoCategoria =
  | { tipo: "ok" }
  | { tipo: "404" }
  | { tipo: "todas" }
  | { tipo: "301"; id: string; slug: string };

/**
 * Decisão que não precisa da API: id que não é número não existe, e o 9999 é a
 * listagem geral (`/colunas`).
 */
export function decidirPeloId(id: string): DecisaoCategoria {
  if (!/^\d+$/.test(id)) return { tipo: "404" };
  if (Number(id) === ID_TODAS_AS_COLUNAS) return { tipo: "todas" };
  return { tipo: "ok" };
}

/**
 * A página do idioma da resposta dá 404: categoria inexistente ou sem artigo
 * nesse idioma. Sem o campo `categoria` (API anterior ao BE-1), não decide.
 * Também serve para o hreflang: o alternate não aponta para um idioma assim
 * (as tags 5, 6 e 8 têm artigos em português e nenhum em inglês).
 */
export function semArtigo(resposta: RespostaColunas): boolean {
  if (!resposta.temMeta) return false;
  const categoria = resposta.categoria;
  return !categoria || !(categoria.total > 0);
}

/**
 * Decisão com a resposta da API: categoria inexistente ou sem artigo no idioma
 * → 404; id ou slug diferentes dos canônicos → 301 para a URL canônica.
 */
export function decidirPelaCategoria(
  resposta: RespostaColunas,
  id: string,
  slug: string,
): DecisaoCategoria {
  if (!resposta.temMeta) return { tipo: "ok" };
  if (semArtigo(resposta)) return { tipo: "404" };

  const categoria = resposta.categoria as CategoriaColuna;
  const idCanonico = String(categoria.id);
  // slug vazio (tag sem nome) não tem URL para onde ir
  if (categoria.slug && (idCanonico !== id || categoria.slug !== slug)) {
    return { tipo: "301", id: idCanonico, slug: categoria.slug };
  }
  return { tipo: "ok" };
}

/**
 * Lê a resposta do `getColumns`. Resposta sem `postsColumns` em lista é falha
 * da API (a página responde 503), por isso lança.
 */
export function lerRespostaColunas(data: unknown): RespostaColunas {
  const corpo = data as { postsColumns?: unknown; categoria?: unknown } | null;
  if (!corpo || typeof corpo !== "object" || !Array.isArray(corpo.postsColumns)) {
    throw new Error("Resposta inesperada do getColumns");
  }

  const temMeta = "categoria" in corpo;
  const categoria =
    corpo.categoria && typeof corpo.categoria === "object"
      ? (corpo.categoria as CategoriaColuna)
      : null;

  return { posts: corpo.postsColumns, categoria, temMeta };
}
